import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Label } from 'ui/label'
import { toast } from 'ui/sonner'
import { Switch } from 'ui/switch'

import { apiClient } from '~/api/client'
import { useIsOnline } from '~/hooks/use-is-online'
import { useSettingsStore } from '~/stores/settings'
import { setupNotifications } from '~/utils/setup-notifications'

import { PushPermissionBlockedDrawer } from './push-permission-blocked-drawer'

export const PushNotificationsToggler = () => {
  const id = useId()
  const [blockedOpen, setBlockedOpen] = useState(false)
  const pushNotificationsEnabled = useSettingsStore(state => state.pushNotificationsEnabled)
  const pushSubscriptionEndpoint = useSettingsStore(state => state.pushSubscriptionEndpoint)
  const { t } = useTranslation()
  const isOnline = useIsOnline()
  const patchSettings = useSettingsStore(state => state.patchSettings)
  const [isLoading, setIsLoading] = useState(false)
  const checked = pushNotificationsEnabled && !!pushSubscriptionEndpoint

  const handleUncheck = async () => {
    if (!pushSubscriptionEndpoint) {
      patchSettings({ pushNotificationsEnabled: false })

      return
    }

    const { response } = await apiClient.POST('/push/toggle-subscription', {
      body: { endpoint: pushSubscriptionEndpoint, enabled: false },
    })

    if (response.status >= 400) {
      toast.error(t('Something went wrong'))

      return
    }

    patchSettings({
      pushNotificationsEnabled: false,
      pushSubscriptionEndpoint: undefined,
    })
  }

  const handleCheck = async () => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
      setBlockedOpen(true)

      return
    }

    if (pushSubscriptionEndpoint) {
      const { response } = await apiClient.POST('/push/toggle-subscription', {
        body: { endpoint: pushSubscriptionEndpoint, enabled: true },
      })

      if (response.status >= 400) {
        toast.error(t('Something went wrong'))

        return
      }

      patchSettings({ pushNotificationsEnabled: true })

      return
    }

    const result = await setupNotifications()
    if (!result.ok) {
      if (result.reason === 'was-denied') setBlockedOpen(true)

      return
    }

    const { response } = await apiClient.POST('/push/toggle-subscription', {
      body: { endpoint: result.endpoint, enabled: true },
    })

    if (response.status >= 400) {
      toast.error(t('Something went wrong'))

      return
    }

    patchSettings({ pushSubscriptionEndpoint: result.endpoint, pushNotificationsEnabled: true })
  }

  return (
    <>
      <section className="flex items-center gap-2">
        <Switch
          checked={checked}
          disabled={!isOnline || isLoading}
          id={id}
          onCheckedChange={async isChecked => {
            try {
              setIsLoading(true)
              if (!isChecked) return await handleUncheck()

              return await handleCheck()
            } catch (error) {
              toast.error(t('Something went wrong'))
              console.error({ message: 'Failed to toggle push notifications', error })
            } finally {
              setIsLoading(false)
            }
          }}
        />
        <Label htmlFor={id}>{t('Push Notifications')}</Label>
      </section>
      <PushPermissionBlockedDrawer onOpenChange={setBlockedOpen} open={blockedOpen} />
    </>
  )
}
